export interface HighlightRegistrierung {
  get(name: string): unknown;
  set(name: string, wert: unknown): void;
  delete(name: string): void;
}

interface HighlightMenge {
  readonly size: number;
  add(bereich: Range): unknown;
  delete(bereich: Range): boolean;
}

const NAME = 'awf-anmerkung';

/**
 * Merkt sich pro Container UND Name, welche Ranges er zur jeweiligen Hervorhebung beigetragen
 * hat -- so kann ein erneuter Aufruf oder das Entfernen nur die eigenen Ranges anfassen, nie die
 * eines anderen Abschnitts. Der Name gehoert zum Schluessel (Kleinkram P2): Zeichnet dasselbe
 * Element unter zwei Namen, ueberschriebe sonst der eine Eintrag den anderen, und dessen Ranges
 * blieben beim naechsten Neuzeichnen stehen.
 */
const gezeichneteBereiche = new WeakMap<Element, Map<string, Range[]>>();

/**
 * Am 2026-09-22 auf Chromium 153 gemessen: vorhanden und sichtbar faerbend. Die Redaktion
 * arbeitet mit Edge oder Chrome, also derselben Grundlage. Faellt die Erkennung trotzdem
 * negativ aus, bleibt die Oberflaeche vollstaendig bedienbar -- nur die Einfaerbung fehlt,
 * die Kommentarliste zeigt weiterhin alles an.
 */
export function holeRegistrierung(): HighlightRegistrierung | null {
  const global = globalThis as {
    CSS?: { highlights?: HighlightRegistrierung };
    Highlight?: unknown;
  };
  if (!global.CSS?.highlights || typeof global.Highlight !== 'function') return null;
  return global.CSS.highlights;
}

/**
 * Teil B: Ein Abschnitt ist kein einzelner Container mehr, sondern mehrere Elemente
 * (`markierung.ts`, `textknotenDesAbschnitts`) -- die Registrierung braucht deshalb einen
 * eigenen Schluessel je Abschnitt statt eines DOM-Containers, ueber den sie ihre eigenen
 * Ranges wiederfindet.
 */
export function zeichneBereiche(
  schluessel: Element,
  bereiche: Range[],
  registrierung: HighlightRegistrierung | null = holeRegistrierung(),
  name: string = NAME,
): number {
  if (!registrierung) return 0;

  let hervorhebung = registrierung.get(name) as HighlightMenge | undefined;
  let jeName = gezeichneteBereiche.get(schluessel);
  if (!jeName) {
    jeName = new Map();
    gezeichneteBereiche.set(schluessel, jeName);
  }
  const vorherige = jeName.get(name);
  if (hervorhebung && vorherige) {
    for (const bereich of vorherige) hervorhebung.delete(bereich);
  }

  if (bereiche.length === 0) {
    jeName.delete(name);
    if (jeName.size === 0) gezeichneteBereiche.delete(schluessel);
    if (hervorhebung && hervorhebung.size === 0) registrierung.delete(name);
    return 0;
  }

  if (!hervorhebung) {
    const Konstruktor = (globalThis as { Highlight?: new () => HighlightMenge }).Highlight;
    if (!Konstruktor) return 0;
    hervorhebung = new Konstruktor();
    registrierung.set(name, hervorhebung);
  }

  for (const bereich of bereiche) hervorhebung.add(bereich);
  jeName.set(name, bereiche);
  return bereiche.length;
}
